import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, LargeCloseIcon, useClickOutside } from '@common-fe/common-fe';

import { FileImage } from '@/common/constants';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import ZoomRotateToolbar from '@/components/controls/DropScan/ZoomRotateToolbar/ZoomRotateToolbar';
import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';

import { useDocumentZoom } from './hooks/useDocumentZoom';
import AddFileButton from './AddFileButton';
import { CloseButton, MainBody, PreviewWrapper, ScanWrapper } from './DocumentPreview.styles';

interface Props {
  onClose: () => void;
  onUploadFile?: () => void;
  onDeleteFile?: (fileId: string) => Promise<unknown>;
  onDeleteDocument?: (documentId: string) => Promise<unknown>;
  isClickOutsideBlocked?: boolean;
}

const DocumentPreview: React.FC<Props> = ({ onClose, onUploadFile, onDeleteFile, onDeleteDocument, isClickOutsideBlocked }) => {
  const { t } = useTranslation();
  const {
    values: { files, documentId },
    handleSetValues,
    handleReset,
  } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);
  const { values, handleSetFormValues } = useMyMoneyFormStore();
  const { setIsOcrCompleted } = useDocumentOcrStore();

  const [activeFileIndex, setActiveFileIndex] = useState(0);

  const {
    rotation,
    isZoomInDisabled,
    isZoomOutDisabled,
    pinchZoomRef,
    previewRef,
    handleZoomIn,
    handleZoomOut,
    handleRotate,
    setRotation,
    setCurrentScale,
  } = useDocumentZoom();

  const allFiles = useMemo(() => (files?.length ? files : []), [files]);
  const hasFiles = files.length > 0;

  const handleSetFiles = useCallback((files: FileImage[]) => {
    handleSetValues({ files });
  }, [handleSetValues]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    const updatedFiles = allFiles.filter((file) => file.id !== fileId);
    await onDeleteFile?.(fileId);
    handleSetValues({ files: updatedFiles });

    if (!updatedFiles?.length) {
      onClose();
      handleReset();
      onDeleteDocument?.(documentId);
      setIsOcrCompleted(false);
      // set suggested values to custom after document is deleted and ocr is no longer available
      handleSetFormValues({
        customAmount: values?.amount,
        customProviderId: values?.providerId,
        customDateOfService: values?.dateOfService,
        customPatientId: values?.patientId,
        customServiceId: values?.serviceId,
      });
    }
  }, [
    onDeleteFile,
    onClose,
    handleSetValues,
    handleReset,
    onDeleteDocument,
    handleSetFormValues,
    setIsOcrCompleted,
    values,
    documentId,
    allFiles,
  ]);

  const handleCloseOnClickOutside = useCallback(() => {
    if (isClickOutsideBlocked) {
      return;
    }
    onClose();
  }, [isClickOutsideBlocked, onClose]);

  useClickOutside(previewRef as React.RefObject<HTMLInputElement>, handleCloseOnClickOutside);

  return (
    <PreviewWrapper ref={previewRef} data-testid="document-preview-wrapper">
      <Box
        pad={{ horizontal: 'spacing12', top: 'spacing12', bottom: 'spacing8' }}
        direction="row"
        justify="end"
        align="center"
        flex={false}
      >
        <CloseButton aria-label={t('Close preview')} onClick={onClose}>
          <LargeCloseIcon color="textOnColor" />
        </CloseButton>
      </Box>
      
      <MainBody
        round={{ corner: 'top', size: 'moduleRound' }}
        pad={{ left: 'spacing12', right: 'spacing12', bottom: 'spacing12' }}
        background="module"
      >
        <ScanWrapper>
          <Box flex="grow" style={{ minHeight: 0, flex: 1 }}>
            <ScanPreviewContainer
              isShowTooltip={false}
              isActiveFileShown
              storeKey={USE_MY_MONEY_UPLOAD_STORE_KEY}
              defaultFiles={allFiles}
              activeFile={activeFileIndex}
              onSetActivePage={(index) => {
                setActiveFileIndex(index);
                setRotation(0);
              }}
              onSetFiles={handleSetFiles}
              handleDeleteFile={handleDeleteFile}
              ocrRequired={false}
              isThumbsTopPosition
              rotation={rotation}
              uploadButton={<AddFileButton onOpenUpload={onUploadFile} />}
              hideShadow
              pinchZoomRef={pinchZoomRef}
              onScaleChange={setCurrentScale}
            />
          </Box>
        </ScanWrapper>

        {hasFiles && (
          <ZoomRotateToolbar
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onRotate={handleRotate}
            isZoomInDisabled={isZoomInDisabled}
            isZoomOutDisabled={isZoomOutDisabled}
          />
        )}
      </MainBody>
    </PreviewWrapper>
  );
};

export default DocumentPreview;
